import { gpGenerateUniqueId } from '#gp/app-common/util/generate-ids';
import { gpMetrics } from '#gp/app-common/util/metrics-logging';
import { gpHome } from '../app';
import {
    isRetryableThumbnailStatus,
    planThumbnailRetryDelayMs,
} from './plan-thumbnail-policy';

let thumbnailImageCount = 0;
const MAX_POLL_TIME_MS = 30000;

interface ThumbnailLoadTelemetry {
    generation: number;
    correlationId: string;
    startedAt: number;
    visibleAt: number | null;
    firstRequestAt: number | null;
    networkMilliseconds: number;
    requestCount: number;
    finalStatus: number;
    decodeStartedAt: number | null;
    hadPreviousThumbnail: boolean;
}

/**
 * Lazy-loads a queued plan thumbnail. A bare img element cannot distinguish a
 * terminal 404 from a 202 that means "the worker is drawing it", nor will it
 * retry either response. This directive owns that HTTP state machine and only
 * assigns img.src once real PNG bytes exist.
 */
gpHome.directive('gpPlanThumbnail', ['$http', '$timeout', function(
    $http: ng.IHttpService,
    $timeout: ng.ITimeoutService,
): ng.IDirective {
    return {
        restrict: 'A',
        link: function(scope: ng.IScope, element: ng.IAugmentedJQuery, attrs: ng.IAttributes) {
            const id = `plan-thumbnail-${thumbnailImageCount++}`;
            element.attr('plan-thumbnail-id', id);

            let sourceUrl = '';
            let pollingUrl = '';
            let visible = false;
            let destroyed = false;
            let attempt = 0;
            let startedAt = 0;
            let requestGeneration = 0;
            let activeGeneration: number | null = null;
            let settledSourceUrl = '';
            let objectUrl: string | null = null;
            let retryPromise: ng.IPromise<void> | null = null;
            let telemetry: ThumbnailLoadTelemetry | null = null;

            // An img with no src can render the browser's broken-image glyph.
            // Preserve its box while hiding it: visibility observation measures
            // this element, so display:none would prevent loading from starting.
            element.css('visibility', 'hidden');

            let visibility: any = null;
            let intersectionObserver: IntersectionObserver | null = null;
            const thumbnailElement = element.get(0);

            if (typeof IntersectionObserver !== 'undefined' && thumbnailElement) {
                // Unlike in-view's window scroll listener, IntersectionObserver
                // also reacts when the card moves inside the plan list scroller.
                intersectionObserver = new IntersectionObserver(entries => {
                    if (entries.some(entry => entry.isIntersecting)) {
                        enterViewport();
                    } else {
                        exitViewport();
                    }
                });
                intersectionObserver.observe(thumbnailElement);
            } else {
                visibility = inView(`[plan-thumbnail-id="${id}"]`)
                    .on('enter', enterViewport)
                    .on('exit', exitViewport);
            }

            attrs.$observe('lazySrc', (value?: string) => {
                const next = value || '';
                if (next === sourceUrl) return;

                finishTelemetry('superseded', 'source-changed');
                sourceUrl = next;
                pollingUrl = next;
                attempt = 0;
                startedAt = 0;
                requestGeneration++;
                cancelRetry();

                if (!sourceUrl) {
                    settledSourceUrl = '';
                    clearDisplayedThumbnail();
                    return;
                }

                beginTelemetry(requestGeneration);

                // Keep the last decoded PNG visible while its replacement is
                // queued and rendered. The new image is swapped in only after
                // an off-DOM Image has proved that its blob can be decoded.
                startIfNeeded();
            });

            scope.$on('$destroy', () => {
                finishTelemetry('destroyed', 'element-destroyed');
                destroyed = true;
                requestGeneration++;
                cancelRetry();
                clearDisplayedThumbnail();
                if (intersectionObserver) {
                    intersectionObserver.disconnect();
                }
                if (visibility && typeof visibility.off === 'function') {
                    visibility.off('enter');
                    visibility.off('exit');
                }
            });

            function enterViewport() {
                visible = true;
                markTelemetryVisible();
                startIfNeeded();
            }

            function exitViewport() {
                visible = false;
                startedAt = 0;
                attempt = 0;
            }

            function startIfNeeded() {
                if (destroyed || !visible || !sourceUrl || settledSourceUrl === sourceUrl) return;

                if (!startedAt) startedAt = Date.now();
                if (activeGeneration === requestGeneration || retryPromise) return;

                const generation = requestGeneration;
                const requestedSourceUrl = sourceUrl;
                activeGeneration = generation;
                requestThumbnail(generation, requestedSourceUrl);
            }

            function requestThumbnail(generation: number, requestedSourceUrl: string) {
                const requestStartedAt = Date.now();
                markTelemetryRequestStarted(generation, requestStartedAt);
                const headers: { [key: string]: string } = { Accept: 'image/png' };
                if (telemetry && telemetry.generation === generation) {
                    headers['X-Correlation-ID'] = telemetry.correlationId;
                }

                $http.get<ArrayBuffer>(pollingUrl, {
                    responseType: 'arraybuffer',
                    cache: false,
                    headers,
                }).then(response => {
                    if (destroyed || generation !== requestGeneration) return;
                    markTelemetryResponse(generation, response.status, requestStartedAt);

                    if (response.status === 200) {
                        markTelemetryDecodeStarted(generation);
                        const contentType = response.headers('Content-Type') || 'image/png';
                        decodeAndDisplay(
                            generation,
                            requestedSourceUrl,
                            new Blob([response.data], { type: contentType }));
                        return;
                    }

                    clearActiveGeneration(generation);
                    if (response.status === 202) {
                        pollingUrl = response.headers('Location') || pollingUrl;
                        scheduleRetry(response.headers('Retry-After'), generation, requestedSourceUrl);
                        return;
                    }

                    fail(generation, requestedSourceUrl, `unexpected-http-${response.status}`);
                }, response => {
                    if (destroyed || generation !== requestGeneration) return;
                    markTelemetryResponse(generation, response.status, requestStartedAt);

                    clearActiveGeneration(generation);
                    if (isRetryableThumbnailStatus(response.status)) {
                        scheduleRetry(response.headers
                            ? response.headers('Retry-After')
                            : null, generation, requestedSourceUrl);
                    } else {
                        fail(generation, requestedSourceUrl, `http-${response.status}`);
                    }
                });
            }

            function decodeAndDisplay(
                generation: number,
                requestedSourceUrl: string,
                bytes: Blob,
            ) {
                const candidateObjectUrl = URL.createObjectURL(bytes);
                const candidate = new Image();

                candidate.onload = () => {
                    clearActiveGeneration(generation);
                    if (destroyed || generation !== requestGeneration) {
                        URL.revokeObjectURL(candidateObjectUrl);
                        return;
                    }

                    if (candidate.naturalWidth < 2) {
                        URL.revokeObjectURL(candidateObjectUrl);
                        fail(generation, requestedSourceUrl, 'tiny-image');
                        return;
                    }

                    const previousObjectUrl = objectUrl;
                    objectUrl = candidateObjectUrl;
                    settledSourceUrl = requestedSourceUrl;
                    element.attr('src', candidateObjectUrl);
                    element.css('visibility', '');

                    // The candidate is already decoded, and the element now
                    // references it. Only now is it safe to release the image
                    // that remained visible during polling.
                    if (previousObjectUrl) {
                        URL.revokeObjectURL(previousObjectUrl);
                    }
                    finishTelemetry('displayed', 'ready');
                };

                candidate.onerror = () => {
                    clearActiveGeneration(generation);
                    URL.revokeObjectURL(candidateObjectUrl);
                    if (!destroyed && generation === requestGeneration) {
                        fail(generation, requestedSourceUrl, 'decode-error');
                    }
                };

                candidate.src = candidateObjectUrl;
            }

            function scheduleRetry(
                retryAfter: string | null,
                generation: number,
                requestedSourceUrl: string,
            ) {
                if (!visible) {
                    return;
                }

                if (Date.now() - startedAt >= MAX_POLL_TIME_MS) {
                    fail(generation, requestedSourceUrl, 'poll-timeout');
                    return;
                }

                const delay = planThumbnailRetryDelayMs(retryAfter, attempt++);
                retryPromise = $timeout(() => {
                    retryPromise = null;
                    if (!destroyed && visible && generation === requestGeneration) {
                        activeGeneration = generation;
                        requestThumbnail(generation, requestedSourceUrl);
                    }
                }, delay, false);
            }

            function fail(
                generation: number,
                requestedSourceUrl: string,
                reason: string,
            ) {
                clearActiveGeneration(generation);
                cancelRetry();
                if (generation === requestGeneration) {
                    settledSourceUrl = requestedSourceUrl;

                    // A refresh failure must not discard the last good image.
                    // With no previous image, keep the img hidden so the card's
                    // fallback is shown without a browser placeholder.
                    if (!objectUrl) {
                        element.css('visibility', 'hidden');
                        element.triggerHandler('error');
                    }

                    finishTelemetry(
                        objectUrl ? 'previous-retained' : 'fallback',
                        reason);
                }
            }

            function cancelRetry() {
                if (retryPromise) {
                    $timeout.cancel(retryPromise);
                    retryPromise = null;
                }
            }

            function clearActiveGeneration(generation: number) {
                if (activeGeneration === generation) {
                    activeGeneration = null;
                }
            }

            function clearDisplayedThumbnail() {
                element.removeAttr('src');
                element.css('visibility', 'hidden');
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                    objectUrl = null;
                }
            }

            function beginTelemetry(generation: number) {
                const now = Date.now();
                telemetry = {
                    generation,
                    correlationId: gpGenerateUniqueId('plan-thumbnail'),
                    startedAt: now,
                    visibleAt: visible ? now : null,
                    firstRequestAt: null,
                    networkMilliseconds: 0,
                    requestCount: 0,
                    finalStatus: -1,
                    decodeStartedAt: null,
                    hadPreviousThumbnail: !!objectUrl,
                };
            }

            function markTelemetryVisible() {
                if (telemetry && telemetry.visibleAt === null) {
                    telemetry.visibleAt = Date.now();
                }
            }

            function markTelemetryRequestStarted(generation: number, startedAt: number) {
                if (!telemetry || telemetry.generation !== generation) return;
                if (telemetry.firstRequestAt === null) {
                    telemetry.firstRequestAt = startedAt;
                }
                telemetry.requestCount++;
            }

            function markTelemetryResponse(
                generation: number,
                status: number,
                requestStartedAt: number,
            ) {
                if (!telemetry || telemetry.generation !== generation) return;
                telemetry.networkMilliseconds += Date.now() - requestStartedAt;
                telemetry.finalStatus = status;
            }

            function markTelemetryDecodeStarted(generation: number) {
                if (telemetry && telemetry.generation === generation) {
                    telemetry.decodeStartedAt = Date.now();
                }
            }

            function finishTelemetry(outcome: string, reason: string) {
                if (!telemetry) return;

                const completed = telemetry;
                telemetry = null;
                if (completed.visibleAt === null && completed.requestCount === 0) {
                    return;
                }

                const now = Date.now();
                gpMetrics().trackEvent('gameplan.plan-thumbnail.load', {
                    correlationId: completed.correlationId,
                    outcome,
                    reason,
                    hadPreviousThumbnail: completed.hadPreviousThumbnail,
                    requestCount: completed.requestCount,
                    pollCount: Math.max(0, completed.requestCount - 1),
                    finalStatus: completed.finalStatus,
                    sourceToVisibleMilliseconds: completed.visibleAt === null
                        ? -1
                        : completed.visibleAt - completed.startedAt,
                    visibleToFirstRequestMilliseconds:
                        completed.visibleAt === null || completed.firstRequestAt === null
                            ? -1
                            : completed.firstRequestAt - completed.visibleAt,
                    networkMilliseconds: completed.networkMilliseconds,
                    decodeMilliseconds: completed.decodeStartedAt === null
                        ? -1
                        : now - completed.decodeStartedAt,
                    totalVisibleMilliseconds: completed.visibleAt === null
                        ? -1
                        : now - completed.visibleAt,
                });
            }
        },
    };
}]);
